import { useState } from 'react'
import { useEditorStore } from '../store/editor.store'
import { api } from '../utils/api'

export interface SiteTheme {
  preset: string
  primaryColor: string
  secondaryColor: string
  bgColor: string
  textColor: string
  fontHeading: string
  fontBody: string
  borderRadius: number
  // Extended tokens (optional — filled by AI or advanced edit)
  surface?: string
  accentMuted?: string
  textMuted?: string
  border?: string
}

export const THEME_PRESETS: SiteTheme[] = [
  {
    preset: 'default',
    primaryColor: '#5b6af0',
    secondaryColor: '#8b5cf6',
    bgColor: '#ffffff',
    textColor: '#1a1a1a',
    fontHeading: '-apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif',
    fontBody: '-apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif',
    borderRadius: 8,
    surface: '#f9fafb',
    textMuted: '#6b7280',
    border: '#e5e7eb',
  },
  {
    preset: 'dark-pro',
    primaryColor: '#6366f1',
    secondaryColor: '#a78bfa',
    bgColor: '#0d0d0d',
    textColor: '#f5f5f5',
    fontHeading: 'Inter, -apple-system, sans-serif',
    fontBody: 'Inter, -apple-system, sans-serif',
    borderRadius: 10,
    surface: '#1a1a1a',
    accentMuted: '#4338ca',
    textMuted: '#9ca3af',
    border: '#2a2a2a',
  },
  {
    preset: 'minimal',
    primaryColor: '#000000',
    secondaryColor: '#444444',
    bgColor: '#ffffff',
    textColor: '#111111',
    fontHeading: '"Helvetica Neue", Helvetica, Arial, sans-serif',
    fontBody: '"Helvetica Neue", Helvetica, Arial, sans-serif',
    borderRadius: 0,
    surface: '#fafafa',
    textMuted: '#6b7280',
    border: '#e5e7eb',
  },
  {
    preset: 'corporate',
    primaryColor: '#1e3a5f',
    secondaryColor: '#2563eb',
    bgColor: '#f8fafc',
    textColor: '#0f172a',
    fontHeading: 'Georgia, "Times New Roman", serif',
    fontBody: '-apple-system, BlinkMacSystemFont, sans-serif',
    borderRadius: 6,
    surface: '#ffffff',
    accentMuted: '#1e40af',
    textMuted: '#64748b',
    border: '#e2e8f0',
  },
  {
    preset: 'warm',
    primaryColor: '#ea580c',
    secondaryColor: '#f97316',
    bgColor: '#fffbf7',
    textColor: '#1c1917',
    fontHeading: '"Playfair Display", Georgia, serif',
    fontBody: '"Lato", -apple-system, sans-serif',
    borderRadius: 12,
    surface: '#fff7ed',
    accentMuted: '#c2410c',
    textMuted: '#78716c',
    border: '#fed7aa',
  },
  {
    preset: 'ocean',
    primaryColor: '#0891b2',
    secondaryColor: '#06b6d4',
    bgColor: '#f0f9ff',
    textColor: '#0c4a6e',
    fontHeading: '"Montserrat", -apple-system, sans-serif',
    fontBody: '"Open Sans", -apple-system, sans-serif',
    borderRadius: 10,
    surface: '#e0f2fe',
    accentMuted: '#0e7490',
    textMuted: '#475569',
    border: '#bae6fd',
  },
  {
    preset: 'forest',
    primaryColor: '#15803d',
    secondaryColor: '#22c55e',
    bgColor: '#f0fdf4',
    textColor: '#14532d',
    fontHeading: '"Merriweather", Georgia, serif',
    fontBody: '-apple-system, BlinkMacSystemFont, sans-serif',
    borderRadius: 8,
    surface: '#dcfce7',
    accentMuted: '#166534',
    textMuted: '#6b7280',
    border: '#bbf7d0',
  },
  {
    preset: 'rose',
    primaryColor: '#e11d48',
    secondaryColor: '#f43f5e',
    bgColor: '#fff1f2',
    textColor: '#881337',
    fontHeading: '"Cormorant Garamond", Georgia, serif',
    fontBody: '"Raleway", -apple-system, sans-serif',
    borderRadius: 14,
    surface: '#ffe4e6',
    accentMuted: '#be123c',
    textMuted: '#9f1239',
    border: '#fecdd3',
  },
  {
    preset: 'midnight',
    primaryColor: '#22d3ee',
    secondaryColor: '#06b6d4',
    bgColor: '#020617',
    textColor: '#e2e8f0',
    fontHeading: '"Space Grotesk", sans-serif',
    fontBody: '"Inter", sans-serif',
    borderRadius: 8,
    surface: '#0f172a',
    accentMuted: '#0e7490',
    textMuted: '#94a3b8',
    border: '#1e293b',
  },
  {
    preset: 'luxury',
    primaryColor: '#b8962e',
    secondaryColor: '#d4af37',
    bgColor: '#0a0a0a',
    textColor: '#f5f0e8',
    fontHeading: '"Cormorant Garamond", Georgia, serif',
    fontBody: '"Raleway", -apple-system, sans-serif',
    borderRadius: 2,
    surface: '#141414',
    accentMuted: '#92741e',
    textMuted: '#a89070',
    border: '#2a2520',
  },
  {
    preset: 'nature',
    primaryColor: '#0d9488',
    secondaryColor: '#14b8a6',
    bgColor: '#f0fdfa',
    textColor: '#134e4a',
    fontHeading: '"Nunito", sans-serif',
    fontBody: '"Nunito", sans-serif',
    borderRadius: 16,
    surface: '#ccfbf1',
    accentMuted: '#0f766e',
    textMuted: '#5eead4',
    border: '#99f6e4',
  },
  {
    preset: 'editorial',
    primaryColor: '#dc2626',
    secondaryColor: '#ef4444',
    bgColor: '#fafafa',
    textColor: '#111827',
    fontHeading: '"Playfair Display", Georgia, serif',
    fontBody: '"Source Sans Pro", sans-serif',
    borderRadius: 4,
    surface: '#f3f4f6',
    accentMuted: '#b91c1c',
    textMuted: '#6b7280',
    border: '#d1d5db',
  },
]

const PRESET_NAMES: Record<string, string> = {
  'default': 'BST Default',
  'dark-pro': 'Dark Pro',
  'minimal': 'Minimal',
  'corporate': 'Corporate',
  'warm': 'Warm Sunset',
  'ocean': 'Ocean Blue',
  'forest': 'Forest Green',
  'rose': 'Rose Luxury',
  'midnight': 'Midnight Tech',
  'luxury': 'Gold Luxury',
  'nature': 'Teal Nature',
  'editorial': 'Editorial Red',
}

interface Props {
  onClose: () => void
}

interface ColorRowProps {
  label: string
  value: string
  onChange: (v: string) => void
}

function ColorRow({ label, value, onChange }: ColorRowProps) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
      <label style={{ fontSize: '13px', color: 'var(--text-secondary)', minWidth: '140px' }}>{label}</label>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
        <input type="color" value={value} onChange={e => onChange(e.target.value)}
          style={{ width: '36px', height: '32px', padding: '2px', border: '1px solid var(--border)', borderRadius: '6px', cursor: 'pointer', background: 'none', flexShrink: 0 }} />
        <input type="text" className="prop-input" value={value}
          onChange={e => onChange(e.target.value)}
          style={{ fontFamily: 'monospace', fontSize: '12px', flex: 1 }} />
      </div>
    </div>
  )
}

export function ThemePickerModal({ onClose }: Props) {
  const { settings, updateSettings } = useEditorStore()
  const currentTheme = settings.themeConfig ? JSON.parse(settings.themeConfig) as SiteTheme : THEME_PRESETS[0]
  const [selected, setSelected] = useState<SiteTheme>(currentTheme)
  const [aiPrompt, setAiPrompt] = useState('')
  const [aiLoading, setAiLoading] = useState(false)
  const [showAdvanced, setShowAdvanced] = useState(false)

  function apply(theme?: SiteTheme) {
    const t = theme ?? selected
    setSelected(t)
    updateSettings({ themeConfig: JSON.stringify(t) })
  }

  function selectPreset(p: SiteTheme) {
    setSelected(p)
    updateSettings({ themeConfig: JSON.stringify(p) })
  }

  function updateField(field: keyof SiteTheme, value: string | number) {
    const updated = { ...selected, [field]: value }
    setSelected(updated)
    updateSettings({ themeConfig: JSON.stringify(updated) })
  }

  async function generateAiTheme() {
    setAiLoading(true)
    try {
      const theme = await api.post<SiteTheme>('/ai/generate-theme', { prompt: aiPrompt })
      const t: SiteTheme = { ...theme, preset: 'ai-generated' }
      setSelected(t)
      updateSettings({ themeConfig: JSON.stringify(t) })
    } catch {
      // silent fail
    } finally {
      setAiLoading(false)
    }
  }

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 9000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={e => { if (e.target === e.currentTarget) onClose() }}
    >
      <div style={{ background: 'var(--bg-1)', border: '1px solid var(--border)', borderRadius: '16px', width: '620px', maxWidth: '95vw', maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 24px 64px rgba(0,0,0,0.3)' }}>
        <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontSize: '16px', fontWeight: 700, color: 'var(--text-primary)' }}>Тема сайта</div>
            <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '2px' }}>Цвета и типографика применяются к опубликованному сайту</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '20px', color: 'var(--text-muted)', cursor: 'pointer', lineHeight: 1 }}>×</button>
        </div>

        <div style={{ padding: '20px 24px', overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: '24px' }}>
          {/* AI theme generator */}
          <div style={{ padding: '16px', background: 'linear-gradient(135deg,rgba(91,106,240,0.06),rgba(155,89,245,0.06))', borderRadius: '12px', border: '1px solid rgba(91,106,240,0.15)' }}>
            <div style={{ fontSize: '12px', fontWeight: 700, color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '10px' }}>✨ AI-генератор темы</div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <input
                className="prop-input"
                value={aiPrompt}
                onChange={e => setAiPrompt(e.target.value)}
                placeholder="Напр: медицинская клиника, элегантная тёмная с золотом, ресторан..."
                onKeyDown={async e => { if (e.key === 'Enter' && aiPrompt.trim() && !aiLoading) await generateAiTheme() }}
                style={{ flex: 1 }}
              />
              <button
                onClick={async () => { if (!aiLoading && aiPrompt.trim()) await generateAiTheme() }}
                disabled={!aiPrompt.trim() || aiLoading}
                style={{ padding: '0 14px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '12px', fontWeight: 700, cursor: aiLoading || !aiPrompt.trim() ? 'default' : 'pointer', opacity: aiLoading || !aiPrompt.trim() ? 0.6 : 1, whiteSpace: 'nowrap' }}
              >{aiLoading ? '⏳' : '✨ Создать'}</button>
            </div>
          </div>

          {/* Preset grid */}
          <div>
            <div style={{ fontSize: '12px', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '12px' }}>Готовые темы</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px' }}>
              {THEME_PRESETS.map(p => {
                const isActive = selected.preset === p.preset
                return (
                  <button key={p.preset} onClick={() => selectPreset(p)}
                    style={{ padding: '0', border: `2px solid ${isActive ? 'var(--accent)' : 'var(--border)'}`, borderRadius: '10px', overflow: 'hidden', cursor: 'pointer', background: 'none', textAlign: 'left' }}>
                    <div style={{ height: '44px', background: `linear-gradient(135deg, ${p.primaryColor}, ${p.secondaryColor ?? p.primaryColor})`, position: 'relative' }}>
                      <div style={{ position: 'absolute', bottom: '6px', right: '8px', width: '14px', height: '14px', borderRadius: '50%', background: p.bgColor, border: '2px solid rgba(0,0,0,0.15)' }} />
                    </div>
                    <div style={{ padding: '5px 7px', background: 'var(--bg-2)' }}>
                      <div style={{ fontSize: '10px', fontWeight: 600, color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{PRESET_NAMES[p.preset] ?? p.preset}</div>
                      <div style={{ width: '100%', height: '2px', borderRadius: '1px', background: p.primaryColor, marginTop: '3px' }} />
                    </div>
                  </button>
                )
              })}
            </div>
          </div>

          {/* Core colors */}
          <div>
            <div style={{ fontSize: '12px', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '12px' }}>Основные цвета</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <ColorRow label="Акцентный (primary)" value={selected.primaryColor} onChange={v => updateField('primaryColor', v)} />
              <ColorRow label="Вторичный (secondary)" value={selected.secondaryColor} onChange={v => updateField('secondaryColor', v)} />
              <ColorRow label="Фон страницы" value={selected.bgColor} onChange={v => updateField('bgColor', v)} />
              <ColorRow label="Цвет текста" value={selected.textColor} onChange={v => updateField('textColor', v)} />
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <label style={{ fontSize: '13px', color: 'var(--text-secondary)', minWidth: '140px' }}>Скругление {selected.borderRadius}px</label>
                <input type="range" min={0} max={24} value={selected.borderRadius} onChange={e => updateField('borderRadius', Number(e.target.value))} style={{ flex: 1 }} />
              </div>
            </div>
          </div>

          {/* Advanced tokens */}
          <div>
            <button onClick={() => setShowAdvanced(v => !v)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '12px', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', padding: 0, display: 'flex', alignItems: 'center', gap: '6px' }}>
              {showAdvanced ? '▾' : '▸'} Расширенные токены
            </button>
            {showAdvanced && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '12px' }}>
                <ColorRow label="Поверхность (cards)" value={selected.surface ?? '#f9fafb'} onChange={v => updateField('surface', v)} />
                <ColorRow label="Приглушённый акцент" value={selected.accentMuted ?? selected.primaryColor} onChange={v => updateField('accentMuted', v)} />
                <ColorRow label="Приглушённый текст" value={selected.textMuted ?? '#6b7280'} onChange={v => updateField('textMuted', v)} />
                <ColorRow label="Цвет рамок" value={selected.border ?? '#e5e7eb'} onChange={v => updateField('border', v)} />
              </div>
            )}
          </div>
        </div>

        <div style={{ padding: '16px 24px', borderTop: '1px solid var(--border)', display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
          <button onClick={onClose}
            style={{ padding: '8px 20px', background: 'var(--bg-2)', color: 'var(--text-secondary)', border: '1px solid var(--border)', borderRadius: '8px', fontSize: '13px', cursor: 'pointer' }}>
            Закрыть
          </button>
          <button onClick={() => { apply(); onClose() }}
            style={{ padding: '8px 20px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: '8px', fontWeight: 600, fontSize: '13px', cursor: 'pointer' }}>
            Применить
          </button>
        </div>
      </div>
    </div>
  )
}

// Generate CSS variables string from theme (used by editor preview)
export function themeToCSS(theme: SiteTheme): string {
  const vars = [
    `--site-primary:${theme.primaryColor}`,
    `--site-secondary:${theme.secondaryColor}`,
    `--site-bg:${theme.bgColor}`,
    `--site-text:${theme.textColor}`,
    `--site-radius:${theme.borderRadius}px`,
    theme.fontHeading ? `--site-font-heading:${theme.fontHeading}` : '',
    theme.fontBody ? `--site-font-body:${theme.fontBody}` : '',
    theme.surface ? `--site-surface:${theme.surface}` : '',
    theme.accentMuted ? `--site-accent-muted:${theme.accentMuted}` : '',
    theme.textMuted ? `--site-text-muted:${theme.textMuted}` : '',
    theme.border ? `--site-border:${theme.border}` : '',
  ].filter(Boolean).join(';')
  return `:root{${vars}}`
}
